/**
 * Página de NOTIFICACIONES (F3-C): lista completa con marcar-leído (individual
 * y todo) + sección de badges. Local-first; el write-path solo permite
 * escribir `read_at` en filas propias.
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Bell, Check } from "lucide-solid";
import { markAllNotificationsReadMutation, markNotificationReadMutation } from "@space/shared";
import type { Notification } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useNavigate } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { PageHeader } from "@/components/ui/page-header";
import { useMyNotifications } from "../../hooks/use-community-data";
import { notificationView } from "./notification-meta";
import { BadgesSection } from "./badges-section";

export const NotificationsView: Component = () => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const navigate = useNavigate();
  const { userId } = useSession();
  const notifications = useMyNotifications();

  const hasUnread = createMemo(() => (notifications.data ?? []).some((n) => !n.read_at));

  const markAll = () => {
    const db = drizzle();
    const uid = userId();
    if (db && uid) {
      void markAllNotificationsReadMutation(db, uid);
    }
  };

  const onPick = (n: Notification) => {
    const db = drizzle();
    if (db && !n.read_at) {
      void markNotificationReadMutation(db, n.id);
    }
    void navigate({ to: notificationView(n).to });
  };

  return (
    <div class="mx-auto max-w-2xl space-y-6 p-4 md:p-6">
      <div class="flex items-center justify-between gap-3">
        <PageHeader title={t(msg`Notifications`)} icon={Bell} />
        <Show when={hasUnread()}>
          <button
            type="button"
            data-testid="notifications-mark-all"
            onClick={markAll}
            class="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-sm font-semibold hover:bg-accent/10"
          >
            <Check class="h-4 w-4" /> {t(msg`Mark all read`)}
          </button>
        </Show>
      </div>

      <ul class="space-y-1.5" data-testid="notifications-list">
        <For
          each={notifications.data ?? []}
          fallback={
            <li class="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              {t(msg`You have no notifications.`)}
            </li>
          }
        >
          {(n) => (
            <li>
              <button
                type="button"
                data-testid="notification-row"
                data-unread={n.read_at ? "false" : "true"}
                onClick={() => onPick(n)}
                class="flex w-full items-start gap-3 rounded-xl border border-border px-3 py-3 text-left text-sm hover:bg-accent/10"
                classList={{ "bg-accent/5": !n.read_at }}
              >
                <span
                  class="mt-1 h-2 w-2 shrink-0 rounded-full"
                  classList={{ "bg-accent": !n.read_at, "bg-transparent": Boolean(n.read_at) }}
                />
                <span class="flex-1" classList={{ "font-semibold": !n.read_at }}>
                  {t(notificationView(n).title)}
                </span>
              </button>
            </li>
          )}
        </For>
      </ul>

      <BadgesSection />
    </div>
  );
};
